import { css } from "@emotion/react";
import { useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { useContext, useEffect, useMemo, useState } from "react";

import { UserPublicationsContext } from "../../contexts/WithPublications/UserPublicationsContext";
import { useConfig } from "../../hooks/useConfig";
import { bounce, pulse } from "./animations";
import { ContentRenderer } from "./ContentRenderer";
import { SourceCards } from "./SourceCards";
import type { Source, UpsellPopoverState } from "./utils";
import { useAISearch } from "./useAISearch";

type AISearchSummaryProps = {
  searchQuery: string;
};

export const AISearchSummary = ({ searchQuery }: AISearchSummaryProps) => {
  const { publicationsBySlug } = useContext(UserPublicationsContext);
  const { assetBucketUrl, aiSearchApiUrl } = useConfig();

  const {
    phase,
    statusStep,
    sources,
    content,
    error,
    visibleSourceCount,
    thinkingInsight,
    abortControllerRef,
    streamSearch,
  } = useAISearch(aiSearchApiUrl);

  const [upsellPopover, setUpsellPopover] = useState<UpsellPopoverState | null>(
    null,
  );
  const {
    refs: floatingRefs,
    floatingStyles,
    context: floatingContext,
  } = useFloating({
    open: upsellPopover !== null,
    onOpenChange: (open) => {
      if (!open) setUpsellPopover(null);
    },
  });
  const floatingDismiss = useDismiss(floatingContext);
  const { getFloatingProps } = useInteractions([floatingDismiss]);

  useEffect(() => {
    if (upsellPopover?.anchorEl) {
      floatingRefs.setReference(upsellPopover.anchorEl);
    }
  }, [upsellPopover, floatingRefs]);

  const getSourceCoverUrl = (s: Source): string | undefined => {
    if (!s.fragment_id) return undefined;
    const slug = s.fragment_id.split("-ch:")[0];
    const pub = publicationsBySlug?.[slug];
    if (pub?.imageUrl) return `${assetBucketUrl ?? ""}/${pub.imageUrl}`;
    return s.cover_url;
  };

  const isLoading =
    phase === "searching" || phase === "sources" || phase === "thinking";
  const showContent = phase === "streaming" || phase === "complete";

  const loadingText = useMemo(() => {
    if (phase === "searching") {
      if (statusStep === "embed") return "Analysing your question...";
      if (statusStep === "search") return "Searching legal sources...";
      return "Searching...";
    }
    if (phase === "thinking") {
      if (statusStep === "analyzing") return "Analysing sources...";
      if (statusStep === "verifying") return "Verifying accuracy...";
      if (statusStep === "answering") return "Writing answer...";
      return "Synthesising answer...";
    }
    return "";
  }, [phase, statusStep]);

  useEffect(() => {
    if (searchQuery && searchQuery.length > 2) {
      setUpsellPopover(null);
      streamSearch(searchQuery);
    }
    const abortControllerRefVar = abortControllerRef;

    return () => {
      abortControllerRefVar.current?.abort();
    };
  }, [searchQuery, streamSearch, abortControllerRef]);

  if (!searchQuery || searchQuery.length <= 2 || !aiSearchApiUrl) {
    return null;
  }

  return (
    <div
      css={({ colours, spacing }) => css`
        background: #fff;
        border: 1px solid ${colours.primary.mid.dark20};
        border-radius: 12px;
        padding: ${spacing(2)};
        margin-bottom: ${spacing(1)};
      `}
    >
      {/* Header row */}
      <div
        css={({ colours, customTypography }) => css`
          ${customTypography.label._2}
          color: ${colours.primary.mid.dark20};
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
        `}
      >
        Compass Insight
        {isLoading && (
          <span
            css={css`
              display: inline-flex;
              gap: 2px;
              align-items: center;
            `}
          >
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                css={css`
                  width: 4px;
                  height: 4px;
                  border-radius: 50%;
                  background: currentColor;
                  animation: ${bounce} 1s ease-in-out infinite;
                  animation-delay: ${i * 0.15}s;
                `}
              />
            ))}
          </span>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div
          css={({ colours, customTypography }) => css`
            ${customTypography.paragraph._2}
            color: ${colours.error.mid.mid};
          `}
        >
          {error}
        </div>
      )}

      {/* Grouped source cards */}
      <SourceCards
        sources={sources}
        visibleSourceCount={visibleSourceCount}
        publicationsBySlug={publicationsBySlug}
        getSourceCoverUrl={getSourceCoverUrl}
        setUpsellPopover={setUpsellPopover}
      />

      {/* Upsell popover for non-owned books */}
      {upsellPopover && (
        <div
          ref={floatingRefs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          css={({ colours }) => css`
            background: #fff;
            border: 1px solid ${colours.primary.light._30};
            border-radius: 8px;
            padding: 12px 16px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            z-index: 10;
            max-width: 260px;
          `}
        >
          <div
            css={({ colours }) => css`
              font-size: 12px;
              line-height: 1.5;
              color: ${colours.primary.dark._20};
            `}
          >
            This book is not included in your current subscription tier.
            <br />
            <strong>{upsellPopover.bookTitle}</strong>
          </div>
          <a
            href={upsellPopover.purchaseUrl}
            target="_blank"
            rel="noopener noreferrer"
            css={({ colours }) => css`
              display: inline-block;
              margin-top: 8px;
              font-size: 12px;
              font-weight: 600;
              color: ${colours.primary.mid.dark20};
              text-decoration: none;

              &:hover {
                text-decoration: underline;
              }
            `}
          >
            {"View on classlegal.com \u2192"}
          </a>
        </div>
      )}

      {/* Loading / thinking state */}
      {isLoading && !error && (
        <div>
          <div
            css={({ colours, customTypography }) => css`
              ${customTypography.paragraph._2}
              color: ${colours.primary.dark._20};
              animation: ${pulse} 1.5s ease-in-out infinite;
            `}
          >
            {loadingText}
          </div>
          {thinkingInsight &&
            phase === "thinking" &&
            statusStep !== "answering" && (
              <div
                css={({ colours }) => css`
                  font-size: 12px;
                  color: ${colours.primary.light._70};
                  margin-top: 4px;
                  transition: opacity 0.3s ease;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                {thinkingInsight}
              </div>
            )}
        </div>
      )}

      {/* Streaming / complete content */}
      {showContent && !error && (
        <ContentRenderer
          content={content}
          sources={sources}
          publicationsBySlug={publicationsBySlug}
          setUpsellPopover={setUpsellPopover}
        />
      )}
    </div>
  );
};
